import { useCallback, useEffect, useState } from 'react';
import Icon from '../app/components/Icon';
import { useSearchParams } from 'react-router';
import FoodReviewPage from './FoodReviewPage';
import UsdaSheet from './UsdaSheet';
import { useApp } from '../app/lib/AppContext';
import { SOURCE_CREDIT } from '../app/lib/foodTable';

// Admin panel › Thực phẩm: Bảng thành phần thực phẩm Việt Nam (2007), the base of every eating calculation. Search
// (accents optional, words in any order, English name, code or alias), filter by group, open a food for every value
// per 100 g, set its cooked factor and the everyday names it's known by.
const INPUT = 'input h-11 w-full rounded-[14px] border-line text-base';
const BUTTON = 'btn h-11 rounded-[14px] border-line bg-base-100 px-4';
const num = (v, digits = 1) => (v == null ? '–' : Number(v).toLocaleString('vi-VN', { maximumFractionDigits: digits }));

export function FoodDetail({ id, onClose, onChanged }) {
  const { api } = useApp();
  const [food, setFood] = useState(null);
  const [alias, setAlias] = useState('');
  const [factor, setFactor] = useState('');
  const [error, setError] = useState(null);
  useEffect(() => { api.get(`/admin/foods/${id}`).then((f) => { setFood(f); setFactor(f.cooked_factor ?? ''); }).catch((e) => setError(e.message)); }, [api, id]);

  const run = async (call) => {
    setError(null);
    try {
      const f = await call();
      setFood(f);
      onChanged?.(f);
    } catch (e) {
      setError(e.message);
    }
  };

  if (!food) return <div className="skeleton h-64 rounded-[20px]">{error}</div>;
  return (
    <section className="flex flex-col gap-4 rounded-[20px] bg-base-100 p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold">{food.name_vi}</h2>
          <p className="text-sm text-muted">
            {food.name_en} · mã {food.code} · {food.group}
            {food.origin === 'usda' ? ` · USDA FDC ${food.nutrients.fdc_id} (${food.nutrients.data_type})`
              : food.origin === 'ai' ? ` · từ ${num(food.nutrients.grams, 0)} g AI ước tính`
                : ` · trang ${food.source_page} · thải bỏ ${num(food.waste_pct)}%`}
          </p>
        </div>
        <button type="button" className="btn btn-circle size-11 border-0 bg-base-200" aria-label="Đóng" onClick={onClose}><Icon name="x" className="size-5" /></button>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center sm:grid-cols-6">
        {[['kcal', 'kcal', 0], ['protein_g', 'Đạm (g)'], ['fat_g', 'Béo (g)'], ['carbs_g', 'Tinh bột (g)'], ['fiber_g', 'Xơ (g)'], ['water_g', 'Nước (g)']].map(([k, label, d]) => (
          <div key={k} className="rounded-[14px] bg-base-200 px-1 py-2">
            <div className="tabular text-lg font-extrabold">{num(food[k], d ?? 1)}</div>
            <div className="text-[11px] text-muted">{label}</div>
          </div>
        ))}
      </div>

      <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); run(() => api.patch(`/admin/foods/${food.id}`, { cooked_factor: factor })); }}>
        <label className="flex flex-col gap-1">
          <span className="text-sm font-semibold">Hệ số chín (khối lượng chín ÷ sống)</span>
          <input type="number" step="0.05" min="0.2" max="4" className={`${INPUT} w-32`} placeholder={String(food.default_factor)}
            value={factor} onChange={(e) => setFactor(e.target.value)} />
        </label>
        <button type="submit" className={BUTTON}>Lưu</button>
        <span className="pb-3 text-xs text-muted">Để trống: theo nhóm ({food.default_factor}). Chỉ áp dụng cho lần tính sau.</span>
      </form>

      <div className="flex flex-col gap-2">
        <span className="text-sm font-semibold">Tên gọi khác (AI và người dùng hay dùng)</span>
        <div className="flex flex-wrap gap-2">
          {food.alias_rows.map((a) => (
            <span key={a.id} className="flex items-center gap-1 rounded-full bg-base-200 py-1 pl-3 pr-1 text-sm">
              {a.name}
              <button type="button" className="grid size-7 place-items-center rounded-full" aria-label={`Bỏ tên ${a.name}`}
                onClick={() => run(() => api.del(`/admin/food_aliases/${a.id}`))}><Icon name="x" className="size-3.5" /></button>
            </span>
          ))}
          {food.alias_rows.length === 0 && <span className="text-sm text-muted">Chưa có.</span>}
        </div>
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (alias.trim()) run(() => api.post(`/admin/foods/${food.id}/aliases`, { name: alias }).then((f) => { setAlias(''); return f; })); }}>
          <input className={INPUT} placeholder="VD: thịt heo" value={alias} onChange={(e) => setAlias(e.target.value)} />
          <button type="submit" className={BUTTON}>Thêm</button>
        </form>
      </div>
      {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}

      {/* A USDA food keeps only its USDA id and dataset there, not nutrients. */}
      {food.origin === 'vn' && Object.keys(food.nutrients).length > 0 && (
        <details className="rounded-[14px] border border-line p-3 text-sm">
          <summary className="cursor-pointer font-semibold">Tất cả thành phần ({Object.keys(food.nutrients).length})</summary>
          <dl className="mt-2 grid grid-cols-[1fr_auto] gap-x-4 gap-y-1">
            {Object.entries(food.nutrients).map(([name, n]) => (
              <div key={name} className="contents"><dt className="text-muted">{name}</dt><dd className="tabular text-right">{num(n.value, 3)} {n.unit}</dd></div>
            ))}
          </dl>
        </details>
      )}
      <p className="text-xs text-muted">{food.origin === 'usda' ? 'Nguồn: USDA FoodData Central'
        : food.origin === 'ai' ? 'Nguồn: AI ước tính, quản trị giữ lại từ Cần xem lại (Bỏ qua Việt Nam và USDA)'
          : `${SOURCE_CREDIT} · ${food.edition}`}</p>
    </section>
  );
}

// Admin panel › Thực phẩm: two tabs, the table itself (search, details, Thêm từ USDA) and "Cần xem lại" (names the
// AI used that matched no food), with the number still open on its tab and in the sidebar.
export default function FoodsPage() {
  const { api } = useApp();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'review' ? 'review' : 'table';
  const [toReview, setToReview] = useState(null);
  const loadCount = useCallback(() => api.get('/admin/food_review_flags?status=open').then((d) => setToReview(d.counts.open || 0)).catch(() => {}), [api]);
  useEffect(() => { loadCount(); }, [loadCount]);
  const tabs = (
    <div role="tablist" className="flex gap-1 rounded-[16px] bg-base-300/70 p-1">
      {[['table', 'Bảng thực phẩm'], ['review', `Cần xem lại${toReview ? ` (${toReview})` : ''}`]].map(([id, label]) => (
        <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setParams(id === 'review' ? { tab: 'review' } : {})}
          className={`h-11 grow rounded-[12px] px-3 text-sm font-semibold ${tab === id ? 'bg-base-100 shadow-sm' : 'text-muted'}`}>
          {label}
        </button>
      ))}
    </div>
  );
  if (tab === 'review') {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-[22px] font-bold">Thực phẩm</h1>
        {tabs}
        <FoodReviewPage onChanged={loadCount} />
      </div>
    );
  }
  return <FoodTablePage tabs={tabs} />;
}

function FoodTablePage({ tabs }) {
  const { api } = useApp();
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState('');
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback((more = false) => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (group) params.set('group', group);
    if (more) params.set('offset', data.foods.length);
    return api.get(`/admin/foods?${params}`)
      .then((res) => setData((prev) => (more ? { ...res, foods: [...prev.foods, ...res.foods] } : res)))
      .catch((e) => setError(e.message));
  }, [api, query, group, data]);
  useEffect(() => { setData(null); load(); }, [query, group]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <h1 className="text-[22px] font-bold">Thực phẩm</h1>
        <button type="button" className={`${BUTTON} shrink-0`} onClick={() => setAdding(true)}>
          <Icon name="plus" className="size-4" /> Thêm từ USDA
        </button>
      </div>
      {tabs}
      <div>
        <p className="text-sm text-muted">Bảng thành phần thực phẩm Việt Nam (2007), giá trị trên 100 g phần ăn được, cùng vài thực phẩm bảng chưa có lấy từ USDA (ức gà, yến mạch, whey…). Mọi phép tính bữa ăn, thực đơn và món đặt ngoài dựa trên đây.</p>
      </div>
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); setQuery(q.trim()); }}>
        <input className={`${INPUT} h-12`} placeholder="Tên (có dấu hoặc không), tên tiếng Anh, mã, tên gọi khác" value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="submit" className={`${BUTTON} h-12 px-5`}>Tìm</button>
      </form>
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        <button type="button" onClick={() => setGroup('')} className={`h-9 shrink-0 rounded-full px-3 text-sm font-semibold ${group === '' ? 'bg-neutral text-neutral-content' : 'bg-base-100'}`}>Tất cả</button>
        {data?.groups.map((g) => (
          <button key={g.code} type="button" onClick={() => setGroup(String(g.code))}
            className={`h-9 shrink-0 whitespace-nowrap rounded-full px-3 text-sm font-semibold ${group === String(g.code) ? 'bg-neutral text-neutral-content' : 'bg-base-100'}`}>
            {g.name} ({g.count})
          </button>
        ))}
      </div>

      <UsdaSheet open={adding} groups={data?.groups || []} onClose={() => setAdding(false)}
        onAdded={(food) => { setAdding(false); setData(null); load(); setOpenId(food.id); }} />
      {openId && <FoodDetail key={openId} id={openId} onClose={() => setOpenId(null)}
        onChanged={(f) => setData((d) => d && { ...d, foods: d.foods.map((r) => (r.id === f.id ? { ...r, aliases: f.alias_rows.map((a) => a.name) } : r)) })} />}
      {error && <div role="alert" className="rounded-[20px] bg-[#fdebe4] p-4 text-sm text-[#8e3414]">{error}</div>}
      {!data && !error && <div className="skeleton h-64 rounded-[20px]" />}
      {data && <p className="text-sm text-muted">{data.total} thực phẩm</p>}
      {data?.foods.length > 0 && (
        <ul className="divide-y divide-line rounded-[20px] bg-base-100 px-4">
          {data.foods.map((f) => (
            <li key={f.id}>
              <button type="button" className="flex w-full items-center gap-3 py-3 text-left" onClick={() => setOpenId(f.id)}>
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold">
                    {f.name_vi} <span className="text-xs font-normal text-muted">· {f.code}</span>
                    {f.origin === 'usda' && <span className="ml-1.5 rounded-full bg-[#e8f0fe] px-2 py-0.5 text-[11px] font-bold text-[#1a56db]">USDA</span>}
                    {f.origin === 'ai' && <span className="ml-1.5 rounded-full bg-[#fdf3d7] px-2 py-0.5 text-[11px] font-bold text-[#8a5a00]">AI ước tính</span>}
                  </span>
                  <span className="block truncate text-xs text-muted">{f.name_en}{f.aliases.length ? ` · ${f.aliases.join(', ')}` : ''}</span>
                </span>
                <span className="tabular shrink-0 text-right text-sm">
                  {num(f.kcal, 0)} kcal
                  <span className="block text-xs text-muted">Đ {num(f.protein_g)} · B {num(f.fat_g)} · TB {num(f.carbs_g)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {data?.more && <button type="button" className={BUTTON} onClick={() => load(true)}>Xem thêm</button>}
    </div>
  );
}
